import React from "react";
import { useTranslation } from "react-i18next";
import { LicensePlan } from "../../../store/actions/PlanActions";
import Button from "../TailwindComponents/Button";
import Badge from "../TailwindComponents/Badge";
import {
  PiCheckLight,
  PiSparkleLight,
  PiUsersLight,
  PiBriefcaseLight,
  PiLightningLight,
  PiHardDriveLight,
  PiShieldCheckLight,
  PiRocketLaunchLight,
  PiCrownLight,
  PiPackageLight,
  PiArrowUpLight,
  PiArrowDownLight,
} from "react-icons/pi";
import { IconType } from "react-icons";

const TIER_ICONS: Record<string, { icon: IconType; color: string }> = {
  starter: { icon: PiShieldCheckLight, color: "text-gray-900 dark:text-gray-100" },
  professional: { icon: PiRocketLaunchLight, color: "text-gray-900 dark:text-gray-100" },
  enterprise: { icon: PiCrownLight, color: "text-gray-900 dark:text-gray-100" },
};

const getTierIcon = (planName: string) => {
  const key = planName.toLowerCase();
  for (const [tier, cfg] of Object.entries(TIER_ICONS)) {
    if (key.includes(tier)) return cfg;
  }
  return { icon: PiPackageLight, color: "text-gray-900 dark:text-gray-100" };
};

/* ── Section grouping ── */
const SECTION_GROUPS: { label: string; keys: string[] }[] = [
  {
    label: "Dashboard",
    keys: ["dashboard", "tasks", "notifications", "calendar", "messages"],
  },
  {
    label: "Workspace",
    keys: ["clients", "matters", "files"],
  },
  {
    label: "Utilities",
    keys: ["research", "tables", "analytics", "data_sources", "templates"],
  },
  {
    label: "Organization",
    keys: ["people", "departments", "teams", "finance", "finances"],
  },
];

const SECTION_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  tasks: "Tasks",
  calendar: "Calendar",
  notifications: "Notifications",
  messages: "Messages",
  clients: "Clients",
  matters: "Matters",
  files: "Files",
  people: "People",
  departments: "Departments",
  teams: "Teams",
  finance: "Finance",
  research: "AI Research",
  tables: "Tables AI",
  client_pipeline: "Client Pipeline",
  analytics: "Analytics",
  data_sources: "Knowledge Base",
  templates: "Templates",
  lawyers: "Lawyer Directory",
  finder: "Finder",
  finances: "Finances",
};

const AI_FEATURE_LABELS: Record<string, string> = {
  drafts: "AI Drafts",
  insights: "AI Insights",
  research: "AI Research",
  tables_ai: "Tables AI",
  onboarding_ai: "AI Onboarding",
  template_ai: "AI Templates",
  data_source_indexing: "Knowledge Indexing",
};

interface PlanCardProps {
  plan: LicensePlan;
  billingPeriod: "monthly" | "annual";
  isCurrentPlan: boolean;
  currentPlanSortOrder?: number;
  onSelect: (plan: LicensePlan) => void;
  onViewDetails: (plan: LicensePlan) => void;
  isLoading?: boolean;
  isOrgAdmin?: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  billingPeriod,
  isCurrentPlan,
  currentPlanSortOrder,
  onSelect,
  onViewDetails,
  isLoading,
  isOrgAdmin = true,
}) => {
  const { t } = useTranslation();
  const price = billingPeriod === "monthly" ? plan.price_monthly : plan.price_annual;
  const gates = plan.feature_gates || { sections: [], ai_features: [], limits: {} };
  const tierCfg = getTierIcon(plan.name || plan.display_name);
  const TierIcon = tierCfg.icon;

  const isDowngrade = !isCurrentPlan && currentPlanSortOrder !== undefined && plan.sort_order < currentPlanSortOrder;
  const actionLabel = isCurrentPlan
    ? t("Current Plan")
    : !isOrgAdmin
      ? t("Request Upgrade")
      : isDowngrade
        ? t("Downgrade")
        : t("Upgrade");

  // Group sections by category
  const sectionSet = new Set(gates.sections);
  const groupedSections = SECTION_GROUPS
    .map((group) => ({
      label: group.label,
      items: group.keys.filter((k) => sectionSet.has(k)),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div
      className={`
        border rounded-lg overflow-hidden bg-white dark:bg-gray-900
        cursor-pointer transition-colors
        ${isCurrentPlan
          ? "border-primary-200 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10 ring-1 ring-primary-500"
          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
        }
      `}
      onClick={() => onViewDetails(plan)}
    >
      <div className="p-5 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <TierIcon className={`h-4 w-4 ${tierCfg.color} flex-shrink-0`} />
            <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
              {plan.display_name}
            </h4>
            {isCurrentPlan && (
              <Badge color="green" className="text-xs">
                {t("Current")}
              </Badge>
            )}
          </div>
          <Button
            variant={isCurrentPlan ? "neutral" : !isOrgAdmin ? "outline" : isDowngrade ? "outline" : "primary"}
            size="small"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onSelect(plan); }}
            disabled={isCurrentPlan || isLoading}
            isLoading={isLoading}
            icon={isCurrentPlan ? undefined : !isOrgAdmin ? undefined : isDowngrade ? <PiArrowDownLight /> : <PiArrowUpLight />}
          >
            {actionLabel}
          </Button>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          {plan.description}
        </p>

        {/* Price */}
        <div className="mb-4">
          <Badge color="green" className="text-sm font-medium px-3 py-1">
            <span className="text-gray-900 dark:text-gray-100">${price.toFixed(0)}</span>
            <span className="text-xs font-normal">
              /{plan.is_per_seat ? t("seat") + "/" : ""}{t("mo")}
            </span>
          </Badge>
          {billingPeriod === "annual" && plan.price_monthly > 0 && (
            <>
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                {t("billed annually")}
              </span>
              <span className="text-xs text-green-600 dark:text-green-400 ml-1">
                {t("Save")} {Math.round(((plan.price_monthly - plan.price_annual) / plan.price_monthly) * 100)}%
              </span>
            </>
          )}
        </div>

        {/* Limits */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
          {gates.limits.max_seats !== undefined && (
            <div className="flex items-center gap-1.5 text-gray-900 dark:text-gray-100">
              <PiUsersLight className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{gates.limits.max_seats === null ? t("Unlimited seats") : `${gates.limits.max_seats} ${t("seats")}`}</span>
            </div>
          )}
          {gates.limits.max_matters !== undefined && (
            <div className="flex items-center gap-1.5 text-gray-900 dark:text-gray-100">
              <PiBriefcaseLight className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{gates.limits.max_matters === null ? t("Unlimited matters") : `${gates.limits.max_matters} ${t("matters")}`}</span>
            </div>
          )}
          {gates.limits.max_ai_queries_monthly !== undefined && (
            <div className="flex items-center gap-1.5 text-gray-900 dark:text-gray-100">
              <PiLightningLight className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{gates.limits.max_ai_queries_monthly === null ? t("Unlimited AI") : `${gates.limits.max_ai_queries_monthly} ${t("AI/mo")}`}</span>
            </div>
          )}
          {gates.limits.max_storage_gb !== undefined && (
            <div className="flex items-center gap-1.5 text-gray-900 dark:text-gray-100">
              <PiHardDriveLight className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{gates.limits.max_storage_gb === null ? t("Unlimited storage") : `${gates.limits.max_storage_gb} GB`}</span>
            </div>
          )}
        </div>

        <hr className="border-gray-200 dark:border-gray-700 mb-4" />

        {/* Sections — grouped by category */}
        <div className="mb-4 flex-1 space-y-3">
          {groupedSections.map((group) => (
            <div key={group.label}>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">
                {t(group.label)}
              </p>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
                {group.items.map((s) => (
                  <li key={s} className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                    <PiCheckLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    {SECTION_LABELS[s] || s}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* AI Features */}
        {gates.ai_features.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">
              {t("AI Features")}
            </p>
            <div className="flex flex-wrap gap-1">
              {gates.ai_features.map((f) => (
                <span
                  key={f}
                  className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded"
                >
                  <PiSparkleLight className="h-3 w-3" />
                  {AI_FEATURE_LABELS[f] || f}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanCard;
